import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, Folder, HardDrive, AlertCircle, Calendar as CalendarIcon, ArrowRight } from 'lucide-react';
import { brandFor } from '../lib/brands';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtSize = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return { label: 'Expired', danger: true, expired: true };
  const days = Math.ceil(diff / 86400000);
  return { label: `${days} day${days === 1 ? '' : 's'} left`, danger: days <= 1, warning: days <= 3 };
};

// Friendly fallback name when title is blank: use the transfer's date
const cardTitleFor = (t) => {
  if (t.title) return t.title;
  const d = t.created_at ? fmtDate(t.created_at) : '';
  return d ? `Files from ${d}` : (t.drive_folder_name || 'Files shared with you');
};

const ClientGalleryPage = () => {
  const { clientToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/transfers/gallery/${clientToken}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Gallery not found');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clientToken]);

  const brand = useMemo(() => brandFor(data?.account_key), [data]);

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-white/60 text-sm">Loading…</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-14 h-14 text-rose-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">Gallery unavailable</h1>
          <p className="text-white/60 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const transfers = data?.transfers || [];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="client-gallery">
      {/* Branded header */}
      <header className="border-b border-white/10 sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-8 h-8 rounded flex items-center justify-center font-bold text-sm shrink-0"
              style={{ background: brand.accent, color: brand.accentText }} data-testid="brand-mark">
              {brand.mark}
            </span>
            <span className="font-semibold tracking-tight truncate" data-testid="brand-name">{brand.name}</span>
            <span className="hidden sm:inline text-white/40 text-xs">· Client Gallery</span>
          </div>
          {data?.email && (
            <div className="text-xs text-white/50 shrink-0 truncate max-w-[50%]" data-testid="client-email">{data.email}</div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 sm:py-12">
        <div className="mb-6 sm:mb-10">
          <div className="text-xs uppercase tracking-wider mb-2" style={{ color: brand.accent }}>Your Gallery</div>
          <h1 className="text-2xl sm:text-4xl font-semibold tracking-tight text-white mb-2">
            {transfers.length > 0
              ? `${transfers.length} transfer${transfers.length === 1 ? '' : 's'} shared with you`
              : 'No active transfers yet'}
          </h1>
          <p className="text-white/60 text-sm sm:text-base">
            {transfers.length > 0
              ? 'Click any card to view and download the files. This link is yours to keep — new transfers will show up here automatically.'
              : 'This is your persistent gallery link. When new files are shared with you, they will appear here automatically.'}
          </p>
        </div>

        {transfers.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-8 sm:p-16 text-center" data-testid="empty-gallery">
            <Folder className="w-14 h-14 mx-auto text-white/20 mb-4" />
            <div className="text-white/60 text-sm">Nothing here yet. Bookmark this page — files will land here as soon as they&apos;re shared with you.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="transfer-cards">
            {transfers.map((t) => {
              const exp = daysUntil(t.expires_at);
              return (
                <Link key={t.share_token} to={`/transfer/${t.share_token}`}
                  className="group rounded-lg overflow-hidden bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] transition-colors block"
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = brand.accentBorder; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = ''; }}
                  data-testid={`transfer-card-${t.share_token}`}>
                  <div className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="text-sm sm:text-base font-semibold text-white line-clamp-2 flex-1">
                        {cardTitleFor(t)}
                      </div>
                      <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors shrink-0 mt-1" />
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-white/60 mb-3">
                      <span className="inline-flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        {fmtDate(t.created_at)}
                      </span>
                      <span className="text-white/20">·</span>
                      <span className="inline-flex items-center gap-1">
                        <HardDrive className="w-3 h-3" />
                        {t.file_count} {t.file_count === 1 ? 'file' : 'files'}
                      </span>
                      <span className="text-white/20">·</span>
                      <span>{fmtSize(t.total_size)}</span>
                    </div>
                    {t.uploader_name && (
                      <div className="text-[11px] text-white/40 mb-3">From {t.uploader_name}</div>
                    )}
                    {exp && (
                      <div className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border ${
                        exp.danger ? 'border-rose-500/40 bg-rose-500/10 text-rose-300' :
                        exp.warning ? 'border-amber-500/40 bg-amber-500/10 text-amber-300' :
                        'border-white/15 bg-white/5 text-white/60'
                      }`} data-testid={`expiry-${t.share_token}`}>
                        <Clock className="w-3 h-3" /> {exp.label}
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <div className="mt-10 sm:mt-16 pt-6 border-t border-white/5 text-xs text-white/40 flex flex-wrap items-center gap-2">
          <span>Powered by</span>
          <span className="font-semibold" style={{ color: brand.accent }}>{brand.name}</span>
          <span className="text-white/20">·</span>
          <span>Bookmark this link — new transfers appear here automatically.</span>
        </div>
      </main>
    </div>
  );
};

export default ClientGalleryPage;
